import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { saveLead } from '../hooks/leads';
import { EmployeeDropdown } from '../components/EmployeeDropDown';
import CompanySelect from '../components/CompanySelect';
import {
  LeadHero,
  LeadPrimaryButton,
  LeadSectionCard,
  LeadShell,
} from './leads/components/leadsUi';

const AddNewLeadPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    first_name: '',
    second_name: '',
    email: '',
    phone: '',
    address: '',
    stage: 'new',
    level_of_interest: 'INTERESTED',
    assigned_to: null as number | null,
    company_id: '',
  });

  const completion = useMemo(
    () => [
      {
        label: 'Name',
        value: formData.first_name ? 'Added' : 'Missing',
      },
      {
        label: 'Contact',
        value: formData.email || formData.phone ? 'Added' : 'Missing',
      },
      {
        label: 'Assignment',
        value: formData.assigned_to ? 'Assigned' : 'Unassigned',
      },
      {
        label: 'Stage',
        value: formData.stage || 'Not set',
      },
    ],
    [formData],
  );

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!orgId) {
      return;
    }

    if (!formData.first_name.trim()) {
      toast({
        title: 'First name is required',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    const response = await saveLead(
      formData.company_id.trim() || null,
      orgId,
      formData.title,
      formData.first_name,
      formData.second_name,
      formData.email,
      formData.phone,
      formData.stage,
      formData.level_of_interest,
      null,
      null,
      null,
      formData.assigned_to,
    );
    setSubmitting(false);

    if (!response.success) {
      toast({
        title: 'Could not save lead',
        description:
          typeof response.data === 'object'
            ? JSON.stringify(response.data, null, 2)
            : `${response.data}`,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Lead saved',
      description: 'The new lead has been added successfully.',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/leads-dashboard`);
  };

  return (
    <LeadShell compact>
      <LeadHero
        title="Add a new lead"
        description="Capture the lead details, assign the right owner, and save it straight into your pipeline."
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <LeadSectionCard
            title="Lead details"
            description="Add the core information the team needs to identify and contact this lead."
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <FormControl>
                <FormLabel>Title</FormLabel>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Mr., Mrs., Dr."
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>First name</FormLabel>
                <Input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Last name</FormLabel>
                <Input
                  name="second_name"
                  value={formData.second_name}
                  onChange={handleChange}
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
            </SimpleGrid>

            <Box mt={5}>
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <PhoneInput
                  country="zw"
                  value={formData.phone}
                  onChange={(phone) =>
                    setFormData((prev) => ({
                      ...prev,
                      phone: phone.replace(/\s+/g, ''),
                    }))
                  }
                  inputStyle={{
                    width: '100%',
                    height: '44px',
                    borderRadius: '16px',
                    border: '1px solid #E2E8F0',
                    paddingLeft: '48px',
                    background: '#F7FAFC',
                  }}
                  buttonStyle={{
                    border: 'none',
                    background: 'transparent',
                    borderTopLeftRadius: '16px',
                    borderBottomLeftRadius: '16px',
                  }}
                  dropdownStyle={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                />
                <FormHelperText>
                  Add at least one contact method so follow-up is easier.
                </FormHelperText>
              </FormControl>
            </Box>
          </LeadSectionCard>

          <LeadSectionCard
            title="Pipeline and ownership"
            description="Choose the current stage, interest level, owner, and related company."
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <FormControl>
                <FormLabel>Stage</FormLabel>
                <Select
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                  borderRadius="16px"
                  bg="gray.50"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Interest level</FormLabel>
                <Select
                  name="level_of_interest"
                  value={formData.level_of_interest}
                  onChange={handleChange}
                  borderRadius="16px"
                  bg="gray.50"
                >
                  <option value="HIGHLY">Highly Interested</option>
                  <option value="MEDIUM_INTEREST">Medium Interest</option>
                  <option value="INTERESTED">Interested</option>
                  <option value="NEUTRAL">Neutral</option>
                  <option value="NOT_INTERESTED">Not Interested</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            <Stack spacing={5} mt={5}>
              <EmployeeDropdown
                organizationId={orgId || ''}
                onChange={(id) =>
                  setFormData((prev) => ({
                    ...prev,
                    assigned_to: id ? Number(id) : null,
                  }))
                }
              />
              <CompanySelect
                onChange={(id) =>
                  setFormData((prev) => ({ ...prev, company_id: id || '' }))
                }
                organizationId={orgId || ''}
              />
            </Stack>
          </LeadSectionCard>
        </Stack>

        <Stack spacing={6}>
          <LeadSectionCard
            title="Save"
            description="Review the lead details and save when ready."
          >
            <VStack align="stretch" spacing={3}>
              <LeadPrimaryButton
                onClick={handleSubmit}
                isLoading={submitting}
                loadingText="Saving"
              >
                Save Lead
              </LeadPrimaryButton>
              <Button borderRadius="16px" variant="ghost" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </VStack>
          </LeadSectionCard>

          <LeadSectionCard
            title="Summary"
            description="A quick view of what has been filled in so far."
          >
            <Stack spacing={4}>
              {completion.map((item) => (
                <Box key={item.label}>
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    color="gray.500"
                    mb={1}
                  >
                    {item.label}
                  </Text>
                  <Text color="gray.900" fontWeight="600">
                    {item.value}
                  </Text>
                </Box>
              ))}
            </Stack>
          </LeadSectionCard>
        </Stack>
      </SimpleGrid>
    </LeadShell>
  );
};

export default AddNewLeadPage;
